import { Divider } from '@mui/material';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import {
  ButtonDiv,
  DescriptDiv,
  TitleDiv,
} from '@components/ConsentOverlay/ConsentOverlay.style';
import ConsentSurvey from '@components/ConsentSurvey';
import ContentLoader from '@components/ContentLoader';
import DisableEnableUserContent from '@components/Modal/Content/DisableEnableUserModalContent/DisableEnableUserContent';
import RoundedButton from '@components/RoundedButton';

import { VITALL_CONSENT } from '@constants/consents';
import { VITALL_PRIVACY, VITALL_TERMS } from '@constants/external-links';

import {
  useCreateConsent,
  useGetConsents,
  useUpdateConsent,
} from '@hooks/api/consent';
import { useGetStatuses } from '@hooks/api/status';
import { useGetUser, useUpdateUserConsent } from '@hooks/api/user';
import { useGetVitallPatientByUserId } from '@hooks/api/vitall-patient';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { getVitallConsentTemplate } from '@models/fhir-templates/consent';

import { StatusType } from 'generated/types';
import { ConsentStatus } from 'generated/types';

const messages = defineMessages({
  buttonText: {
    defaultMessage: 'Submit',
    id: 'GLOBAL.submit',
  },
  requiredText: {
    defaultMessage: 'Required',
    id: 'GLOBAL.requiredText',
  },
  title: {
    defaultMessage:
      'At VITALL, we value your privacy and consent. As such, we regularly review our information management practices. The following consents are required to use VITALL:',
    id: 'consentOverlay.title',
  },
  description: {
    defaultMessage:
      'This consent can be withdrawn at anytime by visiting the consent center within your account settings.',
    id: 'consentOverlay.description',
  },
  errorMessage: {
    id: 'consentForm.errorMessage',
    defaultMessage: 'An error occurred while updating consents',
  },
  vitallMessage: {
    id: 'consentForm.vitallMessage',
    defaultMessage:
      'I{hasName, select, true {, <b>{name}</b>,} other {}} consent to VITALL collecting, storing, and processing my Personally Identifiable Information (“PII”) and Personal Health Information (“PHI”) in accordance with its Terms and Conditions and its Privacy Notice to provide its services.',
  },
  tocPnConsent: {
    id: 'consentForm.tocPnConsent',
    defaultMessage: 'I accept the Terms and Conditions and Privacy Notice.',
  },
  disableTitle: {
    id: 'disableEnableUser.disableTitle',
    defaultMessage: 'Deactivate Account',
  },
});

interface ConsentModalContentProps {
  onClose?: () => void;
}

function ConsentModalContent({ onClose }: ConsentModalContentProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile, isClient },
  } = useSession();
  const [patient_id, user_id, last_login, first_login] = [
    activeProfile?.patient_id,
    activeProfile?.user_id,
    activeProfile?.last_login,
    activeProfile?.first_login,
  ];
  const { data: userData, loading: userLoading } = useGetUser();
  const { data: consentData, loading: consentLoading } = useGetConsents();
  const { data: vitallPatientData, loading: vitallPatientLoading } =
    useGetVitallPatientByUserId({
      id: user_id,
    });
  const { data: statusesData, loading: statusesLoading } = useGetStatuses({
    model: StatusType.VitallPatient_Profile,
  });

  const user = userData?.getUserRecord;
  const activeStatusId = statusesData?.find(
    ({ private_name }) => private_name === 'Active'
  )?.status_id;
  const profileStatusId = vitallPatientData?.getVitallPatientRecord?.status_id;
  const activeStatus = activeStatusId === profileStatusId;
  const handleClose = () => onClose?.();

  {
    /* Show deactivation section if profile status is active or it isn't user's first login */
  }
  const showDeactivationSection =
    isClient &&
    (activeStatus || dayjs(last_login).diff(first_login, 'minutes') > 1);

  const [updateConsent, updateConsentLoading] = useUpdateConsent();
  const [createConsent, createConsentLoading] = useCreateConsent();
  const [updateUserConsent, updateUserConsentLoading] = useUpdateUserConsent();

  const loading =
    updateConsentLoading ||
    createConsentLoading ||
    updateUserConsentLoading ||
    userLoading ||
    consentLoading ||
    vitallPatientLoading ||
    statusesLoading;

  const vitallConsent = consentData.find((d) => d.hasCategory(VITALL_CONSENT));
  const tocPnConsent = userData?.getUserRecord.toc_completed;

  const initialValues: {
    vitall_consent: boolean;
    toc_pn_consent: boolean;
    submit: string | null;
  } = useMemo(
    () => ({
      vitall_consent: vitallConsent?.isActive() ?? false,
      toc_pn_consent: tocPnConsent ?? false,
      submit: null,
    }),
    [tocPnConsent, vitallConsent]
  );

  const validationSchema = Yup.object().shape({
    vitall_consent: patient_id
      ? Yup.boolean()
          .oneOf([true])
          .required(formatMessage(messages.requiredText))
      : Yup.boolean().nullable(),
    toc_pn_consent: Yup.boolean()
      .oneOf([true])
      .required(formatMessage(messages.requiredText)),
  });

  return (
    <ContentLoader loading={loading}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (
              // !activeProfile?.patient_id ||
              // !values.vitall_consent ||
              !values.toc_pn_consent ||
              (patient_id && !values.vitall_consent)
            ) {
              return;
            }
            setSubmitting(true);
            if (!vitallConsent && patient_id) {
              // Consent doesn't exist, so create it
              await createConsent({
                input: {
                  ...getVitallConsentTemplate(patient_id, true),
                },
              });
            } else if (vitallConsent?.id && !vitallConsent.isActive()) {
              // Consent exists, but is not active
              await updateConsent({
                input: {
                  consent_id: vitallConsent.getId(),
                  consent: {
                    ...vitallConsent.getModelInput(),
                    status: ConsentStatus.active,
                  },
                },
              });
            }
            if (user_id) {
              await updateUserConsent({
                input: {
                  user_id,
                  toc_completed: values.toc_pn_consent,
                  pn_completed: values.toc_pn_consent,
                },
              });
            }
          } catch {
            enqueueSnackbar({
              variant: 'error',
              message: formatMessage(messages.errorMessage),
            });
          } finally {
            handleClose();
            setSubmitting(false);
          }
        }}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, errors }) => (
          <Form>
            <TitleDiv data-testid="consent-overlay-notif-title">
              {formatMessage(messages.title)}
            </TitleDiv>
            <DescriptDiv>
              <ConsentSurvey
                error={
                  errors.toc_pn_consent
                    ? formatMessage(messages.requiredText)
                    : undefined
                }
                links={[
                  {
                    text: 'Terms and Conditions',
                    link: VITALL_TERMS,
                  },
                  {
                    text: 'Privacy Notice',
                    link: VITALL_PRIVACY,
                  },
                ]}
                name="toc_pn_consent"
                text={formatMessage(messages.tocPnConsent)}
                validate
              />
              {patient_id && (
                <ConsentSurvey
                  error={
                    errors.vitall_consent
                      ? formatMessage(messages.requiredText)
                      : undefined
                  }
                  links={[
                    {
                      text: 'Terms and Conditions',
                      link: VITALL_TERMS,
                    },
                    {
                      text: 'Privacy Notice',
                      link: VITALL_PRIVACY,
                    },
                  ]}
                  name="vitall_consent"
                  text={formatMessage(messages.vitallMessage, {
                    hasName: false,
                  })}
                  validate
                />
              )}
            </DescriptDiv>
            <DescriptDiv data-testid="consent-overlay-notif-subtitle">
              {formatMessage(messages.description)}
            </DescriptDiv>
            <ButtonDiv>
              <RoundedButton
                disabled={
                  isSubmitting ||
                  Boolean(errors.vitall_consent || errors.toc_pn_consent)
                }
                type="submit"
                width="150px"
              >
                {formatMessage(messages.buttonText)}
              </RoundedButton>
            </ButtonDiv>
          </Form>
        )}
      </Formik>
      {showDeactivationSection && (
        <>
          <Divider>OR</Divider>
          <DisableEnableUserContent
            buttonText={formatMessage(messages.disableTitle)}
            disabled={false}
            email={user?.email}
            onSubmit={handleClose}
          />
        </>
      )}
    </ContentLoader>
  );
}

export default withErrorBoundary(ConsentModalContent, {
  fallback: null,
});
